import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Copy, Loader2 } from "lucide-react";
import { LocationSummary } from "@/services/bulkLocationCopyService";

interface BulkCopyConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  targetLocations: LocationSummary[];
  sourceLocationName: string;
  isLoading: boolean;
}

export const BulkCopyConfirmationModal: React.FC<BulkCopyConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  targetLocations,
  sourceLocationName,
  isLoading
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Confirm bulk copy operation
          </DialogTitle>
          <DialogDescription>
            You are about to copy data from "{sourceLocationName}" to {targetLocations.length} other location{targetLocations.length !== 1 ? 's' : ''} in this organization.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> This will overwrite the following fields in all target locations:
              <ul className="mt-2 ml-4 list-disc text-sm space-y-1">
                <li>Additional Information (waitlist, scholarships)</li>
                <li>Camp Experience (top activities, themes, scales)</li>
                <li>Allergy Accommodation</li>
                <li>Religious Affiliation</li>
                <li>Support for Campers with Disabilities</li>
              </ul>
            </AlertDescription>
          </Alert>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-sm text-gray-900 mb-3">
              Locations that will be updated ({targetLocations.length}):
            </h4>
            <div className="max-h-40 overflow-y-auto">
              <ul className="space-y-1">
                {targetLocations.map((location) => (
                  <li 
                    key={location.id} 
                    className="text-sm text-gray-700 flex items-center gap-2"
                  >
                    <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                    {location.name}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {targetLocations.length === 0 && (
            <Alert>
              <AlertDescription>
                No other locations found in this organization. Nothing to copy.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={isLoading || targetLocations.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Copying...
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copy to {targetLocations.length} location{targetLocations.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 